import type { FC } from 'react';
import { useEffect } from 'react';
import { getClickStreamEvent, getEventAnalytic, useGetClickStreamCashierContext } from 'services/clickstream';
import { FlowTypes } from 'store/constants';
import { useAppSelector } from 'store/hooks';
import type { PayHubCashierContractsReversePendingWithdrawalV2Item } from 'store/models';
import { useReversePendingWithdrawalV2Mutation } from 'store/services/withdrawalRejectAPI';
import { isInPopUpSelector, returnUrlsSelector } from 'store/slices/global';
import {
  pendingWithdrawalsSelector,
  preselectedOptionSelector,
  reversePrecisionSelector,
} from 'store/slices/withdrawalReject';
import { appendCloseButtonActionParam, handleRedirect } from 'utils';

import { setWithdrawalRejectBilletClosedCookies } from '../utils';
import { PartialReverse } from './PartialReverse';
import { PartialReverseResult } from './PartialReverseResult';
import { getReverseErrorType, NO_AMOUNT_RETURNED_ERROR } from './utils';

const clickstream = getEventAnalytic();

interface PartialReverseContainerProps {
  onDeposit: () => void;
}

export const PartialReverseContainer: FC<PartialReverseContainerProps> = ({ onDeposit }) => {
  const cashierContext = useGetClickStreamCashierContext(FlowTypes.withdrawal);
  const pendingWithdrawals = useAppSelector(pendingWithdrawalsSelector);
  const preselectedOption = useAppSelector(preselectedOptionSelector);
  const precision = useAppSelector(reversePrecisionSelector);
  const isOpenedInPopup = useAppSelector(isInPopUpSelector);
  const { merchantUrl, merchantSuccessUrl, merchantRedirectType } = useAppSelector(returnUrlsSelector);

  const [reverse, { data, error, isLoading, isError }] = useReversePendingWithdrawalV2Mutation();
  const isFailed = isError || (data !== undefined && !data.success_amount);

  const handleBackToGame = () => {
    const urlToRedirect = appendCloseButtonActionParam(merchantSuccessUrl || merchantUrl || '', true);

    if (!urlToRedirect) {
      onDeposit();
      return;
    }

    setWithdrawalRejectBilletClosedCookies();
    handleRedirect({
      urlToRedirect,
      isOpenedInPopup,
      merchantRedirectType,
      isInWeb: () => window.self !== window.top,
    });
  };

  useEffect(() => {
    if (!isFailed) {
      return;
    }

    const errorType = isError ? getReverseErrorType(error) : NO_AMOUNT_RETURNED_ERROR;

    clickstream.push(getClickStreamEvent.pw_homepage_popup_cancel_withdrawal_error(errorType), [cashierContext]);
  }, [isFailed, isError, error]);

  const handleReturn = (items: PayHubCashierContractsReversePendingWithdrawalV2Item[]) => {
    reverse({ items });
  };

  if (isFailed) {
    return <PartialReverseResult onDeposit={onDeposit} />;
  }

  if (data?.success_amount) {
    return <PartialReverseResult result={data} onDeposit={onDeposit} onBackToGame={handleBackToGame} />;
  }

  return (
    <PartialReverse
      withdrawals={pendingWithdrawals}
      preselectedOption={preselectedOption}
      precision={precision}
      isFetching={isLoading}
      onReturn={handleReturn}
      onDeposit={onDeposit}
    />
  );
};
